// External libraries
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import get from 'lodash/get';

import LocationImage from 'components/location/image';
import LocationImageHeader from 'components/location/image/header';
import Cancel from 'components/bookings/buttons/cancel';
import ParkingPass from 'components/bookings/buttons/parking-pass';
import ParkingTimes from 'components/bookings/parking-times';

import Brand from 'models/brand';
import BookingModel from 'models/booking';
import EventModel from 'models/event';
import { Location } from 'models/locations';

import VisibilitySensor from 'react-visibility-sensor';
import { pageProps } from 'lib/analytics/page-properties';
import { BUTTON_IMPRESSION, BUTTON_CLICK } from 'lib/analytics/events';

const propTypes = {
  booking: PropTypes.instanceOf(BookingModel).isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  event: PropTypes.instanceOf(EventModel),
  location: PropTypes.instanceOf(Location).isRequired,
  addMessageAndScrollToTop: PropTypes.func.isRequired,
  app: PropTypes.string.isRequired,
  trackEvent: PropTypes.func.isRequired,
  analyticsPosition: PropTypes.number,
};

const defaultProps = {
  event: null,
  analyticsPosition: 0,
};

export const VALID_SHARED_STATUS_DESCRIPTIONS = {
  pending: 'Pending transfer to',
  accepted: 'Transferred to',
};

class Booking extends Component {
  constructor(props) {
    super(props);

    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  onVisibilityChange(isVisible, eventName) {
    const { trackEvent, app, analyticsPosition } = this.props;
    if (isVisible) {
      const pageProperties = pageProps({ app });
      trackEvent({
        ...BUTTON_IMPRESSION,
        ...pageProperties,
        name: eventName,
        position: analyticsPosition.toString(),
      });
    }
  }

  onClick(eventName) {
    const { trackEvent, app, analyticsPosition } = this.props;
    const pageProperties = pageProps({ app });
    trackEvent({
      ...BUTTON_CLICK,
      ...pageProperties,
      name: eventName,
      position: analyticsPosition.toString(),
    });
  }

  renderParkingTimes() {
    if (this.props.booking.type === 'monthly_booking') {
      return this.renderMonthlyStartDate();
    }
    return <ParkingTimes booking={this.props.booking} className="text-size-10 padding-top-20" />;
  }

  renderMonthlyStartDate() {
    const { booking } = this.props;

    return (
      <div className="parking-times">
        <div className="row horizontal-padding">
          <div className="arrival col-xs-4 padding-left-0">Starts</div>
          <div className="col-xs-8 padding-left-0">
            <span className="weak">{booking.startTime.format('ddd MMM D ')}</span>
          </div>
        </div>
      </div>
    );
  }

  renderParkingInfo() {
    const { booking, event } = this.props;
    if (booking.type === 'monthly_booking') {
      const monthlyName = booking.monthlyPricing && booking.monthlyPricing.name ? booking.monthlyPricing.name : 'MONTHLY PARKING PASS';

      return <div className="parking-info wrap-ellipses">{monthlyName}</div>;
    } else if (event) {
      return <div className="parking-info wrap-ellipses weak">{event.name}</div>;
    }

    return null;
  }

  renderBookingShareMessage() {
    const { bookingShare } = this.props.booking;
    const { sharedStatus, sharedWithEmail } = bookingShare;
    const validSharedStatusDescription = get(VALID_SHARED_STATUS_DESCRIPTIONS, sharedStatus, '');

    if (!sharedStatus || !validSharedStatusDescription) {
      return null;
    }


    const classes = cx('booking-share-msg-container text-weight-book', {
      'booking-share-msg-container-pending': bookingShare.isSharedStatusPending,
      'booking-share-msg-container-accepted': bookingShare.isSharedStatusAccepted,
    });

    return (
      <div className={classes}>
        <div>
          <div className="msg-content-row">{validSharedStatusDescription}&nbsp;</div>
          <div className="msg-content-row">{sharedWithEmail}</div>
        </div>
      </div>
    );
  }

  renderCancelButton() {
    const { booking, addMessageAndScrollToTop } = this.props;

    let button = null;
    let onClick = null;
    if (booking.package.id) {
      button = <span className="text-align-center display-block padding-vertical-15 text-size-12">Package</span>;
    } else if (booking.cancellableStatus.cancellableNow) {
      button = <Cancel addMessageAndScrollToTop={addMessageAndScrollToTop} booking={booking} buttonStyle="inline" />;
      onClick = () => this.onClick('cancel');
    }

    return (button &&
      <VisibilitySensor onChange={v => this.onVisibilityChange(v, 'Cancel')} partialVisibility>
        <div className="col-xs-6 col-gutter-0" onClick={onClick}>
          {button}
        </div>
      </VisibilitySensor>);
  }

  renderCancelAndReceiptButtons() {
    const { booking, brand } = this.props;

    const shouldRenderCancelButton = booking.cancellableStatus.cancellableNow || booking.package.id;

    const link = this.props.booking.anonymousReceiptUrl;
    const receiptButtonClasses = cx({
      'border-style-solid': shouldRenderCancelButton,
      'border-side-left': shouldRenderCancelButton,
      'border-color-light-grey': shouldRenderCancelButton,
      'text-align-center': true,
      'display-block': true,
      'padding-vertical-15': true,
      'text-size-12': true,
      'text-color-hover-white': true,
      'background-color-hover-green': !brand.isBestParking,
      'text-color-green': !brand.isBestParking,
      'background-color-hover-bp-blue': brand.isBestParking,
      'text-color-bp-blue': brand.isBestParking,
    });

    const receiptButtonSizeClasses = cx({
      'col-gutter-0': true,
      'col-xs-6': shouldRenderCancelButton,
      'col-xs-12': !shouldRenderCancelButton,
    });

    return (
      <div className="row booking-actions horizontal-padding">
        { shouldRenderCancelButton && this.renderCancelButton() }
        <VisibilitySensor onChange={v => this.onVisibilityChange(v, 'ViewReceipt')} partialVisibility>
          <div onClick={() => this.onClick('ViewReceipt')}>
            <div className={receiptButtonSizeClasses}>
              <a href={link} className={receiptButtonClasses}>
                <span className="iconified-font iconified-pull-ticket margin-right-5" />
                <span>Receipt</span>
              </a>
            </div>
          </div>
        </VisibilitySensor>
      </div>
    );
  }

  render() {
    const { booking, location, brand, app, trackEvent, analyticsPosition } = this.props;

    return (
      <div className="col-lg-4 col-md-6 col-xs-12 booking-card text-color-dark-slate">
        <div className="active-booking heavy-shadowed">
          <div>
            <LocationImage location={location}>
              <LocationImageHeader
                analyticsPosition={analyticsPosition}
                app={app}
                location={location}
                trackEvent={trackEvent}
              />
              {this.renderBookingShareMessage()}
            </LocationImage>
          </div>
          <div className="card-content">
            {this.renderParkingInfo()}
            <div className="booking-row horizontal-padding">
              {this.renderParkingTimes()}
            </div>
          </div>
          { this.renderCancelAndReceiptButtons() }
          <div className="padding-horizontal-10 padding-vertical-20 text-align-center text-size-12 text-weight-book">
            PARKING PASS #<span className="text-weight-bold">{booking.id}</span>
          </div>
        </div>
        <VisibilitySensor onChange={v => this.onVisibilityChange(v, 'ParkingPass')} partialVisibility>
          <div onClick={() => this.onClick('ParkingPass')}>
            <ParkingPass booking={booking} brand={brand} />
          </div>
        </VisibilitySensor>
      </div>
    );
  }
}

Booking.propTypes = propTypes;
Booking.defaultProps = defaultProps;
export default Booking;
